/**
 * The New Connection dialog as the host of the flows that open ON TOP of it (Option C, C3; plan §4 C3 "Import completion
 * callback", D9): Import from another tool (the Azure review included) and Detect local databases save connections
 * themselves, then tell the dialog under them. The dialog decides: with no draft input it closes itself (a completion
 * by import, `ui:connection_dialog_completed_by_import`); otherwise it stays with "Saved 3 connections from DBeaver." —
 * a nested flow never discards a draft.
 *
 * The dialog is modal, so at most one is mounted: it registers on mount (create mode) and unregisters on unmount. No
 * draft, value or credential passes through here — only the completion facts.
 */
import { useRef } from 'react';
import { create } from 'zustand';

export type NestedFlowVia = 'import' | 'azure_review' | 'docker';

export type NestedFlowCompletion = {
  via: NestedFlowVia;
  /** How many connections the flow saved (> 0). */
  count: number;
  /** Where they came from, for the dialog's notice ("DBeaver", "Azure"). */
  sourceLabel: string;
};

export type ConnectionDialogHost = {
  /** Read fresh: whether any draft, or Name / Color / AI access, has input — the dialog then stays open. */
  hasInput: () => boolean;
  /** The flow saved connections: `closed` — the dialog closed itself; `kept` — it stays open and says what was saved. */
  completed: (completion: NestedFlowCompletion) => 'closed' | 'kept';
  /**
   * The flow's dialog is closing (Cancel, Close, Escape, or after a kept completion): focus goes back to the entry that
   * opened it (plan §3.6 "nested focus return"). The flow calls it from its `onCloseAutoFocus` and prevents Radix's own
   * return, which cannot see the entry once the source menu has closed.
   */
  restoreFocus: () => void;
};

type HostState = {
  host: ConnectionDialogHost | null;
  /** Returns the unregister function (a no-op once another dialog took over). */
  register: (host: ConnectionDialogHost) => () => void;
};

export const useConnectionDialogHostStore = create<HostState>((set, get) => ({
  host: null,
  register: (host) => {
    set({ host });
    return () => {
      if (get().host === host) set({ host: null });
    };
  },
}));

/**
 * The host a flow was opened on top of, while it is still the registered one — else null (C3 review C3-05: a late
 * completion, e.g. a held import save that settles after its New Connection dialog closed, goes to nobody, never to a
 * newer dialog that happens to be registered by then).
 */
export function liveHost(captured: ConnectionDialogHost | null): ConnectionDialogHost | null {
  return captured && useConnectionDialogHostStore.getState().host === captured ? captured : null;
}

/**
 * For a flow's dialog: remembers the host registered when `open` turned true (the dialog it opened on top of) and
 * returns a getter for it that answers null once that dialog has gone. The capture outlives the flow's own close — its
 * `onCloseAutoFocus` and a completion reported right after `close()` still need it; the next open replaces it. So an
 * operation that can outlive its open (a held import save) takes `getter()` when it STARTS and checks it with
 * `liveHost` when it settles (C3 review r2, C3-12) — reading the getter then would answer for the next open's host.
 */
export function useOriginatingHost(open: boolean): () => ConnectionDialogHost | null {
  const captured = useRef<ConnectionDialogHost | null>(null);
  const wasOpen = useRef(false);
  if (open && !wasOpen.current) captured.current = useConnectionDialogHostStore.getState().host;
  wasOpen.current = open;
  return () => liveHost(captured.current);
}
